import React, { useCallback, useEffect, useState } from "react";
import Close from "../../../../assets/svgs/Close.svg";
import { ButtonTypes } from "../../../../interfaces/enums/ButtonTypes.enum";
import Button from "../../../atoms/AnimatedButton/AnimatedButton.component";
import Card from "../../../molecules/Card/Card.component";
import SearchField from "../../../molecules/SearchField/SearchField.component";
import { JobSearchFilterGroup } from "../JobSearchFilterAndResults.blok";
import JobSearchFilterSection from "./JobSearchFilterSection/JobSearchFilterSection.component";

interface JobSearchFilterProps {
  heading: string;
  cta_label: string;
  filters: JobSearchFilterGroup[];
  clear_all_button_label: string;
  closeButtonClicked: () => void;
}

const JobSearchFilter: React.FC<JobSearchFilterProps> = ({
  heading,
  cta_label,
  filters,
  closeButtonClicked,
  clear_all_button_label = "reset",
}: JobSearchFilterProps) => {
  const [searchBarInputValue, setSearchBarInputValue] = useState("");
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);

  // Get current URL parameters
  const getUrlParams = () => {
    if (typeof window !== "undefined") {
      const urlParams = new URLSearchParams(window.location.search);
      return {
        search: urlParams.get("search") || "",
        filters: urlParams.getAll("filters") || [],
      };
    }
    return { search: "", filters: [] };
  };

  // Update URL with new parameters
  const updateURL = useCallback((newSearch: string, newFilters: string[]) => {
    if (typeof window !== "undefined") {
      const url = new URL(window.location.href);

      // Update search parameter
      if (newSearch.trim()) {
        url.searchParams.set("search", newSearch);
      } else {
        url.searchParams.delete("search");
      }

      // Update filters parameter
      url.searchParams.delete("filters"); // Clear existing filters
      newFilters.forEach(filter => {
        url.searchParams.append("filters", filter);
      });

      // Use replaceState to update URL without navigation/scrolling
      window.history.replaceState({}, "", `${url.pathname}${url.search}`);

      // Dispatch a custom event to trigger the filter update
      window.dispatchEvent(new CustomEvent("urlchange"));
    }
  }, []);

  // Load initial state from URL
  useEffect(() => {
    const { search, filters } = getUrlParams();
    setSearchBarInputValue(search);
    setSelectedFilters(filters);
  }, []);

  // Update search bar value
  const updateSearchBarInputValue = useCallback(
    (value: string) => {
      setSearchBarInputValue(value);
      updateURL(value, selectedFilters);
    },
    [selectedFilters, updateURL],
  );

  // Update selected filters
  const updateSelectedFilters = useCallback(
    (newFilters: string[]) => {
      setSelectedFilters(newFilters);
      updateURL(searchBarInputValue, newFilters);
    },
    [searchBarInputValue, updateURL],
  );

  // Clear all filters
  const clearAllFilters = useCallback(() => {
    setSearchBarInputValue("");
    setSelectedFilters([]);
    updateURL("", []);
  }, [updateURL]);

  return (
    <>
      <Card isExpandable={false}>
        <div id="job-search-filter-header">
          <div className="flex justify-between w-full items-center">
            <h2 className="flex justify-between text-2xl xl:text-3xl 2xl:text-4xl font-extrabold pb-4 text-Primary-900 border-b border-dashed w-full">
              {heading}
              <button
                className="font-bold text-SP4 uppercase whitespace-nowrap ml-4 mr-4 md:mr-0 text-base"
                onClick={clearAllFilters}
                aria-label="Clear all filters"
              >
                {clear_all_button_label}
              </button>
            </h2>
            <button className="md:hidden pb-4" onClick={() => closeButtonClicked()} aria-label="Close Filter">
              <Close className="" />
            </button>
          </div>

          <div className="pt-4">
            <SearchField onChange={updateSearchBarInputValue} value={searchBarInputValue} />
          </div>
        </div>

        <div id="job-search-filter-main-content">
          {filters.map((filter, idx) => (
            <div key={idx}>
              <JobSearchFilterSection
                filter_section={filter}
                selectedFilters={selectedFilters}
                updateSelectedFilters={updateSelectedFilters}
              />
            </div>
          ))}
        </div>

        {/* <div className="pt-4">
          <Button styles={'bg-White border border-Primary-900'} label={cta_label} type={ButtonTypes.PRIMARY} fullWidth={true} />
        </div> */}
      </Card>
    </>
  );
};

export default JobSearchFilter;
